/**
 * Public Sign Page — Smart Consents™
 * Route: /sign/:token  (NO AUTH — patient-facing, mobile-first)
 *
 * Wizard flow:
 *   1. Welcome screen with clinic branding
 *   2. One template per step → read + tap "Ho letto e accetto"
 *   3. Single signature pad at end
 *   4. Success screen with animation
 *
 * Design language: Apple Health / fintech (Revolut, Stripe Checkout).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import SignaturePad from "../components/dental/SignaturePad";
import {
  ShieldCheck, Loader2, ChevronRight, ChevronLeft, CheckCircle2,
  AlertTriangle, FileSignature, Sparkles, Eraser, Lock,
} from "lucide-react";

const PublicSignPage = () => {
  const { token } = useParams();
  const [state, setState] = useState("loading");  // loading|ready|signed|expired|error
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  // Wizard
  const [step, setStep] = useState(0);            // 0 = welcome, 1..N = templates, N+1 = sign
  const [agreed, setAgreed] = useState({});       // template_id -> bool
  const [submitting, setSubmitting] = useState(false);
  const [doneAt, setDoneAt] = useState(null);

  const padRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/dental/consents/public/${token}`);
        setData(r.data);
        setState("ready");
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 410) {
          if (typeof detail === "object" && detail?.signed_at) {
            setDoneAt(detail.signed_at);
            setState("signed");
          } else {
            setState("expired");
          }
        } else if (status === 404) {
          setState("error");
          setError("Link non valido o non più disponibile.");
        } else {
          setState("error");
          setError("Impossibile caricare i moduli. Riprova più tardi.");
        }
      }
    })();
  }, [token]);

  const templates = data?.templates || [];
  const totalSteps = 1 + templates.length + 1; // welcome + templates + sign
  const allAgreed = templates.every(t => agreed[t.template_id]);

  const goNext = () => setStep(s => Math.min(s + 1, totalSteps - 1));
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const submit = async () => {
    const url = padRef.current?.getDataURL();
    if (!url || padRef.current?.isEmpty()) {
      alert("Per favore firma nello spazio dedicato.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/dental/consents/public/${token}/sign`, {
        signature_data_url: url,
      });
      setDoneAt(r.data.signed_at);
      setState("signed");
    } catch (err) {
      alert(err.response?.data?.detail || "Errore durante l'invio. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ============= STATES ============= */
  if (state === "loading") {
    return <ScreenWrap><LoadingScreen /></ScreenWrap>;
  }
  if (state === "error") {
    return <ScreenWrap>
      <StatusCard
        icon={<AlertTriangle className="w-8 h-8 text-amber-500" />}
        title="Link non valido"
        body={error || "Il link che hai aperto non è più valido."}
        accent="amber"
      />
    </ScreenWrap>;
  }
  if (state === "expired") {
    return <ScreenWrap>
      <StatusCard
        icon={<AlertTriangle className="w-8 h-8 text-amber-500" />}
        title="Link scaduto"
        body="Il termine per la firma è scaduto. Contatta lo studio per ricevere un nuovo link."
        accent="amber"
      />
    </ScreenWrap>;
  }
  if (state === "signed") {
    return <ScreenWrap>
      <SuccessScreen at={doneAt} tenant={data?.tenant_name} />
    </ScreenWrap>;
  }

  /* ============= WIZARD ============= */
  const tenantName = data.tenant_name || "Studio Odontoiatrico";
  const isWelcome = step === 0;
  const isSignStep = step === totalSteps - 1;
  const tplIdx = step - 1;
  const currentTpl = !isWelcome && !isSignStep ? templates[tplIdx] : null;
  const stepLabel = isWelcome
    ? "Benvenuto"
    : isSignStep
    ? "Firma"
    : `Modulo ${tplIdx + 1} di ${templates.length}`;
  const progress = ((step + 1) / totalSteps) * 100;

  return (
    <ScreenWrap>
      <div className="w-full max-w-xl" data-testid="public-sign-wizard">
        {/* Top bar with clinic + progress */}
        <header className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 text-white flex items-center justify-center shadow-lg">
              <FileSignature className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Firma digitale</p>
              <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{tenantName}</p>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {stepLabel}
            </span>
          </div>
          <div className="h-1 w-full rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
              data-testid="sign-progress-bar"
            />
          </div>
        </header>

        {/* CARD */}
        <div
          key={step}
          className="rounded-3xl bg-white dark:bg-slate-900/80 backdrop-blur border border-slate-200/70 dark:border-white/10 shadow-xl shadow-slate-900/5 p-6 sm:p-8 animate-in fade-in slide-in-from-bottom-3 duration-500"
        >
          {isWelcome && (
            <WelcomeStep data={data} onStart={goNext} />
          )}
          {currentTpl && (
            <TemplateStep
              t={currentTpl}
              total={templates.length}
              index={tplIdx}
              agreed={!!agreed[currentTpl.template_id]}
              onAgree={(v) => setAgreed(a => ({ ...a, [currentTpl.template_id]: v }))}
              onBack={goBack}
              onNext={goNext}
            />
          )}
          {isSignStep && (
            <SignStep
              padRef={padRef}
              hasInk={hasInk}
              setHasInk={setHasInk}
              onBack={goBack}
              onSubmit={submit}
              submitting={submitting}
              allAgreed={allAgreed}
              clientName={data.client_name}
              templates={templates}
            />
          )}
        </div>

        {/* Footer trust */}
        <div className="mt-5 flex items-center justify-center gap-2 text-[11px] text-slate-400">
          <Lock className="w-3 h-3" />
          Protetto da SmartBooker · GDPR compliant
        </div>
      </div>
    </ScreenWrap>
  );
};

/* ============================================================ */
/* WRAPPERS                                                     */
/* ============================================================ */
const ScreenWrap = ({ children }) => (
  <div className="min-h-screen w-full bg-gradient-to-b from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-black flex items-center justify-center p-4 sm:p-6">
    {/* Glow background */}
    <div className="pointer-events-none fixed inset-0 overflow-hidden">
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-violet-400/20 blur-[120px] rounded-full" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-cyan-400/20 blur-[120px] rounded-full" />
    </div>
    <div className="relative z-10 w-full flex items-center justify-center">
      {children}
    </div>
  </div>
);

const LoadingScreen = () => (
  <div className="w-full max-w-md text-center">
    <div className="inline-flex w-14 h-14 rounded-3xl bg-gradient-to-br from-violet-500 to-cyan-500 items-center justify-center shadow-xl mb-4">
      <Loader2 className="w-6 h-6 text-white animate-spin" />
    </div>
    <p className="text-slate-500 dark:text-slate-400 font-medium">Sto preparando i tuoi moduli…</p>
  </div>
);

const StatusCard = ({ icon, title, body, accent = "violet" }) => {
  const ring = accent === "amber"
    ? "from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30"
    : "from-violet-100 to-cyan-100 dark:from-violet-900/30 dark:to-cyan-900/30";
  return (
    <div className="w-full max-w-md text-center rounded-3xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/10 p-8 shadow-xl shadow-slate-900/5">
      <div className={`mx-auto w-16 h-16 rounded-3xl bg-gradient-to-br ${ring} flex items-center justify-center mb-4`}>
        {icon}
      </div>
      <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">{title}</h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
};

const SuccessScreen = ({ at, tenant }) => {
  const when = at ? new Date(at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : null;
  return (
    <div className="w-full max-w-md text-center" data-testid="sign-success-screen">
      <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-2xl shadow-emerald-500/30 mb-6 animate-in zoom-in-50 duration-500">
        <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2.5} />
      </div>
      <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
        Grazie!
      </h1>
      <p className="text-slate-500 dark:text-slate-400 mb-6">
        I tuoi moduli sono stati firmati e archiviati in sicurezza.
      </p>
      <div className="inline-flex flex-col items-center gap-1 px-5 py-4 rounded-2xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/10">
        {when && (
          <>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Firmato il</p>
            <p className="font-bold text-slate-900 dark:text-white">{when}</p>
          </>
        )}
        {tenant && <p className="text-xs text-slate-500 mt-1">{tenant}</p>}
      </div>
      <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
        <ShieldCheck className="w-3.5 h-3.5" />
        Conservato in modo cifrato · GDPR
      </div>
    </div>
  );
};

/* ============================================================ */
/* STEPS                                                        */
/* ============================================================ */
const WelcomeStep = ({ data, onStart }) => (
  <div className="text-center space-y-5">
    <div className="mx-auto w-16 h-16 rounded-3xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
      <Sparkles className="w-8 h-8 text-white" />
    </div>
    <div>
      <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
        Ciao{data.client_name ? `, ${data.client_name.split(" ")[0]}` : ""}!
      </h1>
      <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm sm:text-base">
        Lo studio <strong className="text-slate-700 dark:text-slate-200">{data.tenant_name}</strong> ti chiede
        di firmare digitalmente <strong>{(data.templates || []).length} {(data.templates || []).length === 1 ? "modulo" : "moduli"}</strong>.
        Bastano un paio di minuti.
      </p>
    </div>

    <div className="grid grid-cols-3 gap-2 text-left">
      <Bullet num="1" label="Leggi" />
      <Bullet num="2" label="Accetta" />
      <Bullet num="3" label="Firma" />
    </div>

    <button
      onClick={onStart}
      data-testid="sign-start-btn"
      className="w-full h-12 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-sm hover:opacity-90 active:scale-[0.98] transition-all inline-flex items-center justify-center gap-2 shadow-lg"
    >
      Inizia <ChevronRight className="w-4 h-4" />
    </button>
    <p className="text-[10px] text-slate-400 flex items-center justify-center gap-1">
      <Lock className="w-3 h-3" /> I tuoi dati sono protetti e crittografati.
    </p>
  </div>
);

const Bullet = ({ num, label }) => (
  <div className="rounded-2xl bg-slate-50 dark:bg-white/5 p-3 text-center">
    <div className="w-7 h-7 mx-auto rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/10 flex items-center justify-center text-xs font-black text-violet-500">
      {num}
    </div>
    <p className="text-[11px] font-semibold mt-1.5 text-slate-600 dark:text-slate-300">{label}</p>
  </div>
);

const TemplateStep = ({ t, total, index, agreed, onAgree, onBack, onNext }) => (
  <div className="space-y-5" data-testid={`sign-template-step-${index}`}>
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-500">
        {t.category || "Modulo"}
      </p>
      <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight mt-1">
        {t.name}
      </h2>
    </div>

    <div className="max-h-[42vh] overflow-y-auto rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-white/5 p-4 sm:p-5">
      <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap text-justify">
        {t.body}
      </p>
    </div>

    <label
      className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
        agreed
          ? "border-emerald-400 bg-emerald-50/70 dark:bg-emerald-900/20"
          : "border-slate-200 dark:border-white/10 hover:border-slate-300"
      }`}
      data-testid={`sign-agree-checkbox-${index}`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={agreed}
        onChange={e => onAgree(e.target.checked)}
      />
      <div className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all ${
        agreed
          ? "bg-emerald-500 border-emerald-500"
          : "border-slate-300 dark:border-white/20"
      }`}>
        {agreed && <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
      </div>
      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 leading-snug">
        Ho letto e accetto i termini del modulo.
      </span>
    </label>

    <div className="flex gap-2 pt-1">
      <button
        onClick={onBack}
        className="h-12 px-4 rounded-2xl bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-300 font-bold text-sm inline-flex items-center gap-1 hover:bg-slate-200 dark:hover:bg-white/10 transition-all"
        data-testid="sign-step-back-btn"
      >
        <ChevronLeft className="w-4 h-4" /> Indietro
      </button>
      <button
        disabled={!agreed}
        onClick={onNext}
        data-testid="sign-step-next-btn"
        className="flex-1 h-12 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-sm hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {index + 1 < total ? "Avanti" : "Vai alla firma"} <ChevronRight className="w-4 h-4" />
      </button>
    </div>
    <p className="text-[10px] text-center text-slate-400">
      {index + 1} / {total}
    </p>
  </div>
);

const SignStep = ({ padRef, hasInk, setHasInk, onBack, onSubmit, submitting, allAgreed, clientName, templates }) => {
  const clear = () => {
    padRef.current?.clear();
    setHasInk(false);
  };
  return (
    <div className="space-y-4" data-testid="sign-final-step">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-500">Ultimo passaggio</p>
        <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight mt-1">
          Firma qui sotto
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Usa il dito o lo stilo. La firma vale per tutti i {templates.length} moduli accettati.
        </p>
      </div>

      <div className="relative">
        <SignaturePad ref={padRef} height={220} />
        {!hasInk && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-300 dark:text-slate-600 text-sm font-semibold tracking-wide">
            ✍️ firma qui
          </div>
        )}
        <button
          onClick={clear}
          className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-white/90 dark:bg-slate-800/90 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:bg-white"
          data-testid="sign-clear-btn"
        >
          <Eraser className="w-3 h-3" /> Pulisci
        </button>
      </div>

      <div
        onTouchStart={() => setHasInk(true)}
        onMouseDown={() => setHasInk(true)}
        className="h-0 -mt-4 pointer-events-none"
      />

      {clientName && (
        <p className="text-xs text-slate-500 text-center">
          A nome di <strong className="text-slate-700 dark:text-slate-200">{clientName}</strong>
        </p>
      )}

      <div className="flex gap-2">
        <button
          onClick={onBack}
          disabled={submitting}
          className="h-12 px-4 rounded-2xl bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-300 font-bold text-sm inline-flex items-center gap-1 hover:bg-slate-200 dark:hover:bg-white/10 transition-all disabled:opacity-40"
          data-testid="sign-back-final-btn"
        >
          <ChevronLeft className="w-4 h-4" /> Indietro
        </button>
        <button
          disabled={submitting || !allAgreed}
          onClick={onSubmit}
          data-testid="sign-submit-btn"
          className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-bold text-sm hover:opacity-95 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 shadow-lg shadow-violet-500/30"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {submitting ? "Invio in corso…" : "Conferma e firma"}
        </button>
      </div>
      {!allAgreed && (
        <p className="text-[11px] text-center text-amber-600 dark:text-amber-400">
          Devi prima accettare tutti i moduli per poter firmare.
        </p>
      )}
    </div>
  );
};

export default PublicSignPage;
